<template>
    <div class="card shadow mb-3 bg-white rounded balise-info-card">
        <div class="card-body">

            <!-- Display -->
            <div v-if="!updatingData">
                <div class="balise-header">
                    <div class="balise-header-main">
                        <div class="balise-vehicle-block">
                            <VehicleNameDisplay :balise="baliseInfo" :showStatuses="true" :linkToMap="true" />
                        </div>

                        <div class="balise-position-block">
                            <div class="position-eyebrow">Dernière position</div>
                            <div class="position-top">
                                <div class="position-date-block">
                                    <div class="position-date">
                                        {{ baliseInfo.displayLastSeen }}
                                    </div>

                                    <div class="position-ago">
                                        {{ $filters.timeAgo(baliseInfo.lastSeen) }}
                                    </div>
                                </div>
                            </div>

                            <div class="position-address">
                                {{ baliseInfo.lastAddress || 'Adresse non renseignée' }}
                            </div>

                            <div class="position-coordinates">
                                <LatLongDisplay :latitude="baliseInfo.lastLatitude"
                                    :longitude="baliseInfo.lastLongitude" />
                            </div>
                        </div>
                    </div>
                </div>

                <hr class="section-separator" />

                <div class="section-line-title mb-3">
                    <span>Balise</span>

                    <button v-if="!isOperator && !isViewer" class="balise-edit-button" type="button"
                        v-on:click="startUpdating()">
                        <ButtonDisplay :type="'edit'" />
                    </button>
                </div>

                <div class="info-grid">

                    <div class="info-line">
                        <span class="info-label">Modèle</span>
                        <span class="info-value">
                            {{ baliseTypesDisplay[baliseInfo.type] || baliseInfo.type || 'Non renseigné' }}
                        </span>
                    </div>

                    <div class="info-line">
                        <span class="info-label">IMEI</span>
                        <span class="info-value">
                            <IMEIDisplay :imei="baliseInfo.imei" />
                        </span>
                    </div>

                    <div v-if="baliseInfo.serviceDate != null" class="info-line">
                        <span class="info-label">Mise en service</span>
                        <span class="info-value">
                            {{ baliseInfo.serviceDate }}
                        </span>
                    </div>

                    <div v-if="baliseInfo.lastTemperature != null" class="info-line">
                        <span class="info-label">Temp. système</span>
                        <span class="info-value">
                            <TemperatureDisplay :temperature="baliseInfo.lastTemperature" />
                        </span>
                    </div>

                    <div class="info-line">
                        <span class="info-label">Tag</span>
                        <span class="info-value">
                            {{ yesNoDisplay(baliseInfo.hasTag) }}
                        </span>
                    </div>

                    <div class="info-line">
                        <span class="info-label">+ après contact</span>
                        <span class="info-value">
                            {{ yesNoDisplay(baliseInfo.plusAContact) }}
                        </span>
                    </div>

                    <div v-if="baliseInfo.displayLastTagPresentAt" class="info-line">
                        <span class="info-label">Dernier badge présent</span>
                        <span class="info-value">
                            {{ baliseInfo.displayLastTagPresentAt }}
                        </span>
                    </div>

                    <div v-if="baliseInfo.displayLastTagAbsentAt" class="info-line">
                        <span class="info-label">Dernier badge absent</span>
                        <span class="info-value">
                            {{ baliseInfo.displayLastTagAbsentAt }}
                        </span>
                    </div>
                </div>

                <hr class="section-separator" />

                <div class="section-line-title mb-3">
                    <span>Configuration</span>
                </div>

                <div class="info-grid">
                    <div class="info-line">
                        <span class="info-label">Télésurveillance</span>

                        <span class="info-value">
                            {{ baliseInfo.remoteMonitoring == 1 ? 'Active' : 'Désactivée' }}
                        </span>
                    </div>

                    <div class="info-line">
                        <span class="info-label">Offre commerciale</span>

                        <span class="info-value">
                            {{
                                baliseInfo.commercialPackageId == null
                                    ? 'Non renseignée'
                                    : baliseInfo.commercialPackage.name
                            }}
                        </span>
                    </div>

                    <div v-if="baliseInfo.free == 1" class="info-line">
                        <span class="info-label">Facturation</span>

                        <span class="info-value">
                            Balise non facturable
                        </span>
                    </div>
                </div>
            </div>

            <!-- Update -->
            <div v-else class="container">
                <div class="row d-flex justify-content-between my-2">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-name">Nom</span>

                            <input class="form-control" type="text" v-model="updatedName" aria-describedby="input-name"
                                required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-between my-2">
                    <div class="col-12">
                        <div class="input-group">
                            <label class="input-group-text" for="inputGroupSelect01">
                                Groupe
                            </label>

                            <select class="form-select" id="inputGroupSelect01" v-model="updatedGroup" required>
                                <option :value="null">
                                    Aucun groupe
                                </option>

                                <option v-for="group in groups" :key="group" :value="group[1].id">
                                    {{ group[0] }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-immat">
                                Immatriculation
                            </span>

                            <input class="form-control" type="text" v-model="updatedImmat"
                                aria-describedby="input-immat" required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <label class="input-group-text" for="inputGroupSelectType">
                                Type
                            </label>

                            <select class="form-select" id="inputGroupSelectType" v-model="updatedType" required>
                                <option :value="null">Aucun type</option>
                                <option :value="'VOITURE'">Voiture</option>
                                <option :value="'SCOOTER'">Scooter</option>
                                <option :value="'MOTO'">Moto</option>
                                <option :value="'CAMION'">Camion</option>
                                <option :value="'CAMIONETTE'">Camionette</option>
                                <option :value="'REMORQUE'">Remorque</option>
                                <option :value="'AUTRE'">Autre</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-make">
                                Marque
                            </span>

                            <input class="form-control" type="text" v-model="updatedMake" aria-describedby="input-make"
                                maxlength="30" required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-model">
                                Modèle
                            </span>

                            <input class="form-control" type="text" v-model="updatedModel"
                                aria-describedby="input-model" maxlength="30" required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-color">
                                Couleur
                            </span>

                            <input class="form-control" type="text" v-model="updatedColor"
                                aria-describedby="input-color" maxlength="30" required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <p>Selectionnez l'icône :</p>

                        <img v-if="baliseInfo" class="border rounded pointer-cursor"
                            :src="`/img/cars/${baliseInfo.icon}`" :alt="baliseInfo.icon"
                            v-on:click="$emit('changeBalise', baliseInfo.id); $emit('setBaliseToUpdate', baliseInfo.id)"
                            data-bs-toggle="modal" data-bs-target="#iconPicker">
                    </div>
                </div>

                <hr class="my-4" />

                <div class="d-flex justify-content-center">
                    <button class="btn btn-primary m-1"
                        v-on:click="updateData(updatedName, updatedImmat, updatedGroup, updatedType, updatedMake, updatedModel, updatedColor); updatingData = false">
                        Mettre à jour
                    </button>

                    <button class="btn btn-light m-1" v-on:click="updatingData = false">
                        Annuler
                    </button>
                </div>
            </div>
            <!-- End Update -->

        </div>
    </div>
</template>

<script>
import ButtonDisplay from '@/components/display/ButtonDisplay.vue'
import VehicleNameDisplay from '@/components/display/VehicleNameDisplay.vue'
import LatLongDisplay from '@/components/display/LatLongDisplay.vue'
import IMEIDisplay from '@/components/display/IMEIDisplay.vue'
import TemperatureDisplay from '@/components/display/TemperatureDisplay.vue'
import BaliseService from '@/services/balise.service'
import UserHelper from '@/helpers/user.helper.js'

export default {
    props: ['baliseInfo', 'groups'],
    components: {
        VehicleNameDisplay,
        LatLongDisplay,
        TemperatureDisplay,
        IMEIDisplay,
        ButtonDisplay,
    },
    data() {
        return {
            updatingData: false,
            baliseTypesDisplay: {
                LL301: "GEONEO",
                TLW2: "GEOTRACK W2",
                PIONEERX100: "GEOTRACK P100",
                FMB920: "GEOTRACK B92",
                FMC920: "GEOTRACK C92",
                FMC130: "GEOTRACK C13",
                KNIGHTX100: "GEOTRACK X100",
                FMC800: "GEOBD 2",
                TWIGONE: "TWIG ONE",
                TT10: "TT10",
                TLW212B: "GEO TRAILER",
                NS10: "NS10",
                NS20: "NS20",
                GH5200: "GH5200"
            }
        };
    },
    computed: {
        isViewer() {
            return UserHelper.isViewer(this.$store.state.auth.user);
        },
        isOperator() {
            return UserHelper.isOperator(this.$store.state.auth.user);
        }
    },
    watch: {
        baliseInfo(newBI) {
            if (this.updatingData) return
            this.updatedName = newBI.name
            this.updatedImmat = newBI.immat
            this.updatedGroup = newBI.groupId
            this.updatedType = newBI.vehicle
            this.updatedMake = newBI.make
            this.updatedModel = newBI.model
            this.updatedColor = newBI.color
        }
    },
    methods: {
        yesNoDisplay(value) {
            if (value === true || value === 1) return 'Oui';
            if (value === false || value === 0) return 'Non';
            return 'Non renseigné';
        },
        startUpdating() {
            this.updatedName = this.baliseInfo.name
            this.updatedImmat = this.baliseInfo.immat
            this.updatedGroup = this.baliseInfo.groupId
            this.updatedType = this.baliseInfo.vehicle
            this.updatedMake = this.baliseInfo.make
            this.updatedModel = this.baliseInfo.model
            this.updatedColor = this.baliseInfo.color
            this.updatingData = true
        },
        updateData(name, immat, groupId, type, make, model, color) {
            BaliseService.updateData(this.baliseInfo.id, {
                name,
                registrationPlate: immat,
                groupId,
                vehicle: type,
                make,
                model,
                color
            })
                .then(() => {
                    this.$emit("refresh")
                })
                .catch(e => {
                    console.log(e)
                })
        }
    }
}
</script>

<style scoped>
.balise-info-card {
    position: relative;
    overflow: hidden;
    border: 1px solid #e5eaf1;
    border-radius: 17px;
    background: #fff;
}

.balise-info-card .card-body {
    padding: 1.25rem;
}

.balise-header {
    padding: 1.2rem;
    border: 1px solid #e4ebf6;
    border-radius: 15px;
    background: linear-gradient(130deg, #f7faff 0%, #f1f6fe 100%);
}

.balise-header-main {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(205px, 0.85fr);
    gap: 1.1rem;
    align-items: center;
}

.balise-vehicle-block {
    min-width: 0;
    max-width: 100%;
}

.balise-vehicle-block :deep(.vehicle-card) {
    width: 100%;
    justify-content: flex-start !important;
}

.balise-position-block {
    min-width: 0;
    padding-left: 1rem;
    border-left: 1px solid #dce7f5;
}

.position-eyebrow {
    margin-bottom: 0.55rem;
    color: #6081a9;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.075em;
    text-transform: uppercase;
}

.position-top {
    display: flex;
    align-items: flex-start;
}

.position-date-block {
    min-width: 0;
    text-align: left;
}

.position-date {
    color: #283f5d;
    font-size: 0.84rem;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.position-ago {
    margin-top: 0.18rem;
    color: #7b8ba0;
    font-size: 0.76rem;
    line-height: 1.35;
}

.position-address {
    margin-top: 0.65rem;
    color: #4a617c;
    font-size: 0.86rem;
    line-height: 1.5;
    overflow-wrap: anywhere;
}

.position-coordinates {
    margin-top: 0.55rem;
    color: #7790ab;
    font-size: 0.76rem;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.position-coordinates :deep(*) {
    color: inherit;
    font-size: inherit;
    line-height: inherit;
}

.section-separator {
    height: 1px;
    margin: 1.25rem 0 1.05rem;
    border: 0;
    background: #e9edf4;
    opacity: 1;
}

.section-line-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.9rem !important;
    color: #213550;
    font-size: 1.02rem;
    font-weight: 750;
    line-height: 1.3;
}

.section-line-title > span:first-child {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
}

.section-line-title > span:first-child::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 17px;
    border-radius: 9px;
    background: #3d7bcc;
}

.balise-edit-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: transparent;
}

.balise-edit-button :deep(.btn) {
    border-radius: 9px;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.7rem;
}

.info-line {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    min-width: 0;
    min-height: 65px;
    gap: 0.3rem;
    padding: 0.72rem 0.83rem;
    border: 1px solid #edf1f6;
    border-radius: 11px;
    background: #f9fbfd;
}

.info-label {
    color: #8190a3;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.3;
}

.info-value {
    min-width: 0;
    color: #30445f;
    font-size: 0.87rem;
    font-weight: 650;
    line-height: 1.4;
    text-align: left;
    overflow-wrap: anywhere;
}

.balise-info-card :deep(.input-group-text) {
    min-width: 120px;
    color: #63768e;
    font-size: 0.86rem;
    font-weight: 600;
}

.balise-info-card :deep(.input-group > .form-control),
.balise-info-card :deep(.input-group > .form-select) {
    min-width: 0;
}

@media (max-width: 1399.98px) and (min-width: 1200px),
       (max-width: 850px) {
    .balise-header-main {
        grid-template-columns: minmax(0, 1fr);
    }

    .balise-position-block {
        padding-top: 0.85rem;
        padding-left: 0;
        border-top: 1px solid #dce7f5;
        border-left: 0;
    }
}

@media (max-width: 575.98px) {
    .balise-info-card .card-body {
        padding: 0.85rem;
    }

    .balise-header {
        padding: 0.85rem;
    }

    .info-grid {
        gap: 0.55rem;
    }

    .info-line {
        min-height: 62px;
        padding: 0.7rem;
    }
}

@media (max-width: 440px) {
    .info-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .balise-info-card :deep(.input-group-text) {
        min-width: 0;
    }
}
</style>
